import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageProductsView.styles";
import ProductModel from "../../../models/products/ProductModel";
import * as ProductsAction from "../../../stores/products/ProductsAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IManageProductsViewProps extends WithStyles<typeof styles> {}

const ManageProductsViewPure: React.FunctionComponent<IManageProductsViewProps> = ({
    classes
}: IManageProductsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const isRequesting = useSelector(state => selectRequesting(state, [ProductsAction.REQUEST_PRODUCTS]));

    const countries = useSelector(state => selectVisibleCountries(state));
    const productsFromState = useSelector(state => state.products?.products);

    let products: IUniversalTableData;
    if (productsFromState) {
        products = Object.values(productsFromState)
            ?.filter((product: ProductModel) => product.visible)
            ?.map((product: ProductModel) => ({
                id: product.id,
                name: product.name,
                excludedCountries: product.excludedCountries
            }));
    }

    const handleEditFinished = (index, newData) => {
        const productConfig = {
            excludedCountries: newData.excludedCountries
        };
        dispatch(ProductsAction.editProduct(products[index].id, productConfig)).then(res => {
            enqueueSnackbar("Product edited", {
                variant: ToastStatusEnum.Info
            });
            dispatch(ProductsAction.requestProducts());
        });
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.name"),
            field: "name",
            editable: "onAdd"
        },
        {
            type: "countries",
            title: "Unavailable in",
            field: "excludedCountries",
            multiple: true,
            allCountries: countries.map(country => country.name)
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "products.write"
                }
            ]
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs items={[{ title: t("admin.adminPanel"), href: "/admin" }, { title: "Manage products" }]} />
            <div className={classes.title}>
                <Typography variant="h4">Manage products</Typography>
            </div>
            <UniversalTable columns={columns} data={products} isLoading={isRequesting} searchable />
        </CenteredContainer>
    );
};

export const ManageProductsView = withStyles(styles)(ManageProductsViewPure);
